import * as React from 'react';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import useBodyScrollLock from 'hooks/useBodyScrollLock';
import classNames from 'classnames';

import ArrowDown from 'components/icons/ArrowDown';
import ArrowUp from 'components/icons/ArrowUp';

type Layout = 'FORM' | 'CURRENCY';

export type SelectOptionType = {
  value: string;
  name?: string;
  html?: React.ReactElement;
};

interface SelectProps {
  label: string;
  value: string;
  name: string;
  options: SelectOptionType[];
  onChange: (name: string, value: string) => void;
  upperCaseOptions?: boolean;
  layout?: Layout;
}

export default function Select({
  label,
  value,
  name,
  options,
  onChange,
  upperCaseOptions = false,
  layout = 'FORM',
}: SelectProps) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { disableScroll, enableScroll } = useBodyScrollLock();
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    disableScroll();
  };

  const handleClose = () => {
    setAnchorEl(null);
    enableScroll();
  };

  const handleSelect = (selectedValue: string) => {
    onChange(name, selectedValue);
    handleClose();
  };

  const getPresentedValue = () => {
    const selectedOption = options.find(option => option.value === value);
    return selectedOption?.name || selectedOption?.html || value;
  };

  return (
    <div className={`select-container${open ? ' select-container--open' : ''}`}>
      <div className="select-container__label">{label}</div>
      <Button
        id="basic-button"
        disableRipple
        className={classNames({
          'form-layout': layout === 'FORM',
          'currency-layout': layout === 'CURRENCY',
        })}
        onClick={handleMenuOpen}
        endIcon={
          open ? (
            <ArrowUp width={12} height={6} />
          ) : (
            <ArrowDown width={12} height={6} />
          )
        }
      >
        {getPresentedValue()}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          sx: {
            borderTop: '1px solid #000',
            minWidth: '140px',
          },
        }}
      >
        {options.map(option => (
          <MenuItem
            key={option.value}
            disableRipple
            selected={option.value === value}
            onClick={() => handleSelect(option.value)}
          >
            <span
              style={{
                textTransform: upperCaseOptions ? 'uppercase' : 'none',
              }}
            >
              {option.name || option.html || option.value}
            </span>
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
}
